import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Lock, Search, X } from 'lucide-react';
import { dependencies, dependencyInfo } from './data/dependencies';
import InfoTooltip from './components/common/InfoTooltip';

/**
 * Products Step - Matrix view by Domain
 *
 * STRUCTURE:
 * - Columns (Products): Reports-EBS | Reports-Cloud | Analytics
 * - Rows (Modules): Individual modules by domain
 *
 * For HCM domain: Analytics = People Analytics (PEPM pricing)
 * For FIN domain: Analytics = Financial Analytics (fixed pricing)
 */
const ProductsStepNested = ({ quoteData, setQuoteData, pricing }) => {

  const [expandedDomains, setExpandedDomains] = React.useState({
    hcm: false,
    fin: false,
    scm: false,
    crm: false
  });

  const [expandedSections, setExpandedSections] = React.useState({
    erpConnectors: true,
    dataPipelines: false,
    utilities: false,
    sqlConnect: false,
    glConnect: false
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter function for search
  const matchesSearch = (text) => {
    if (!searchQuery.trim()) return true;
    return text.toLowerCase().includes(searchQuery.toLowerCase());
  };

  // Existing product helpers (for existing customers)
  const isExistingCustomer = quoteData.customerType === 'existing';

  const toggleExisting = (sku) => {
    const current = quoteData.existingProducts || [];
    const updated = current.includes(sku)
      ? current.filter(s => s !== sku)
      : [...current, sku];
    setQuoteData({ ...quoteData, existingProducts: updated });
  };

  const isExistingProduct = (sku) => {
    return (quoteData.existingProducts || []).includes(sku);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // DEPENDENCY MANAGEMENT
  // Calculate which connectors/pipelines are required based on selected modules
  // ═══════════════════════════════════════════════════════════════════════════

  // Get all selected SKUs across all product types
  const getAllSelectedSkus = () => {
    const skus = [];

    // Cloud reports
    (quoteData.reportsCloud || []).forEach(name => {
      const item = Object.values(pricing.reportsCloud || {}).flat().find(i => i.name === name);
      if (item?.sku) skus.push(item.sku);
    });

    // EBS reports
    (quoteData.reportsEBS || []).forEach(name => {
      const item = Object.values(pricing.reportsEBS || {}).flat().find(i => i.name === name);
      if (item?.sku) skus.push(item.sku);
    });

    // GL Connect
    if (quoteData.glConnectCloud && pricing.glConnect?.cloud?.sku) {
      skus.push(pricing.glConnect.cloud.sku);
    }
    if (quoteData.glConnectOnPremise && pricing.glConnect?.onPremise?.sku) {
      skus.push(pricing.glConnect.onPremise.sku);
    }

    // User licenses (for Excel Connect dependency)
    if ((quoteData.users?.admin || 0) > 0) skus.push('BE:USER_ADMIN_DEV');
    if ((quoteData.users?.readOnly || 0) > 0) skus.push('BE:USER_READ_ACCESS');
    if ((quoteData.users?.mobile || 0) > 0) skus.push('BE:USER_MOBILE');

    return skus;
  };

  // Calculate required dependencies based on selections
  const requiredDependencies = useMemo(() => {
    const required = {};
    const selectedSkus = getAllSelectedSkus();

    // Check each dependency category
    const allDeps = { ...dependencies.reportsCloud, ...dependencies.reportsEBS, ...dependencies.glConnect, ...dependencies.users };

    selectedSkus.forEach(sku => {
      const deps = allDeps[sku] || [];
      deps.forEach(depSku => {
        if (!required[depSku]) {
          required[depSku] = [];
        }
        // Find the product name for this SKU
        let productName = sku;
        // Look up friendly name from pricing
        Object.values(pricing.reportsCloud || {}).flat().forEach(item => {
          if (item.sku === sku) productName = item.name;
        });
        Object.values(pricing.reportsEBS || {}).flat().forEach(item => {
          if (item.sku === sku) productName = item.name;
        });
        if (pricing.glConnect?.cloud?.sku === sku) productName = 'GL Connect Cloud';
        if (pricing.glConnect?.onPremise?.sku === sku) productName = 'GL Connect EBS';

        required[depSku].push(productName);
      });
    });

    return required;
  }, [quoteData.reportsCloud, quoteData.reportsEBS, quoteData.glConnectCloud, quoteData.glConnectOnPremise, quoteData.users, pricing]);

  // Auto-add/remove required dependencies to quoteData
  useEffect(() => {
    const updates = {};
    let hasChanges = false;

    // Check ERP Connectors - add missing required ones
    const currentConnectors = quoteData.erpConnectors || [];
    const requiredConnectorSkus = Object.keys(requiredDependencies).filter(sku => sku.startsWith('CONN:'));

    // Add missing required connectors
    requiredConnectorSkus.forEach(sku => {
      const item = (pricing.erpConnectors || []).find(i => i.sku === sku);
      if (item && !currentConnectors.includes(item.name)) {
        if (!updates.erpConnectors) updates.erpConnectors = [...currentConnectors];
        updates.erpConnectors.push(item.name);
        hasChanges = true;
      }
    });

    // Check Data Pipelines - add missing required ones
    const currentPipelines = quoteData.dataPipelines || [];
    const requiredPipelineSkus = Object.keys(requiredDependencies).filter(sku => sku.startsWith('PIPE:'));

    // Add missing required pipelines
    requiredPipelineSkus.forEach(sku => {
      const item = (pricing.dataPipelines || []).find(i => i.sku === sku);
      if (item && !currentPipelines.includes(item.name)) {
        if (!updates.dataPipelines) updates.dataPipelines = [...currentPipelines];
        updates.dataPipelines.push(item.name);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setQuoteData(prev => ({ ...prev, ...updates }));
    }
  }, [requiredDependencies, pricing]);

  // Check if a connector/pipeline is locked (required by selected products)
  const isItemLocked = (sku) => {
    return !!requiredDependencies[sku];
  };

  // Get the products that require this dependency
  const getLockedReason = (sku) => {
    const dependents = requiredDependencies[sku] || [];
    if (dependents.length === 0) return null;
    if (dependents.length <= 2) {
      return `Required for: ${dependents.join(', ')}`;
    }
    return `Required for: ${dependents.slice(0, 2).join(', ')} +${dependents.length - 2} more`;
  };

  const domainLabels = {
    hcm: 'HCM Reports and Analytics',
    fin: 'FIN Reports and Analytics',
    scm: 'Supply Chain (SCM) and Manufacturing (MFG)',
    crm: 'Customer Relationship Management (CRM)'
  };

  const toggleDomain = (domain) => {
    // Auto-collapse other domains and all sections when opening a new one
    setExpandedDomains(prev => ({
      hcm: domain === 'hcm' ? !prev.hcm : false,
      fin: domain === 'fin' ? !prev.fin : false,
      scm: domain === 'scm' ? !prev.scm : false,
      crm: domain === 'crm' ? !prev.crm : false,
    }));
    // Collapse all other sections
    setExpandedSections({
      erpConnectors: false,
      dataPipelines: false,
      utilities: false,
      sqlConnect: false,
      glConnect: false
    });
  };

  const toggleSection = (section) => {
    // Auto-collapse domains and other sections when opening a section
    setExpandedDomains({
      hcm: false,
      fin: false,
      scm: false,
      crm: false
    });
    setExpandedSections(prev => ({
      erpConnectors: section === 'erpConnectors' ? !prev.erpConnectors : false,
      dataPipelines: section === 'dataPipelines' ? !prev.dataPipelines : false,
      utilities: section === 'utilities' ? !prev.utilities : false,
      sqlConnect: section === 'sqlConnect' ? !prev.sqlConnect : false,
      glConnect: section === 'glConnect' ? !prev.glConnect : false,
    }));
  };

  // Get all unique module names across EBS and Cloud for a domain
  const getModulesForDomain = (domain) => {
    const ebsItems = pricing.reportsEBS?.[domain] || [];
    const cloudItems = pricing.reportsCloud?.[domain] || [];

    // Get analytics items for this domain
    let analyticsItems = [];
    if (domain === 'hcm') {
      analyticsItems = pricing.peopleAnalytics || [];
    } else if (domain === 'fin') {
      analyticsItems = pricing.financialAnalytics || [];
    }

    // Build a map of normalized name -> { displayName, ebs, cloud, analytics }
    const moduleMap = {};

    // Add EBS modules
    ebsItems.forEach(item => {
      const key = normalizeForMatching(item.name);
      if (!moduleMap[key]) {
        moduleMap[key] = { displayName: cleanDisplayName(item.name), sortKey: item.name };
      }
      moduleMap[key].ebs = item;
    });

    // Add Cloud modules
    cloudItems.forEach(item => {
      const key = normalizeForMatching(item.name);
      if (!moduleMap[key]) {
        moduleMap[key] = { displayName: cleanDisplayName(item.name), sortKey: item.name };
      } else {
        // Prefer cloud display name if available
        moduleMap[key].displayName = cleanDisplayName(item.name);
      }
      moduleMap[key].cloud = item;
    });

    // Add Analytics modules - try to match to existing rows or add new
    analyticsItems.forEach(item => {
      const key = normalizeForMatching(item.name);

      // Try to find a matching key
      const existingKey = Object.keys(moduleMap).find(k =>
        k === key ||
        k.includes(key) ||
        key.includes(k) ||
        fuzzyMatch(k, key)
      );

      if (existingKey) {
        moduleMap[existingKey].analytics = item;
      } else {
        // Add as new row
        moduleMap[key] = {
          displayName: item.name,
          sortKey: item.name,
          analytics: item
        };
      }
    });

    // Sort by display name and return as array
    return Object.values(moduleMap).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  };

  // Clean display name - remove parenthetical codes
  const cleanDisplayName = (name) => {
    return name.replace(/\s*\([^)]*\)\s*$/, '').trim();
  };

  // Normalize name for matching
  const normalizeForMatching = (name) => {
    return name
      .toLowerCase()
      .replace(/\s*\([^)]*\)\s*/g, '') // Remove parenthetical codes
      .replace(/human resources/gi, 'hr')
      .replace(/general ledger/gi, 'gl')
      .replace(/accounts payables?/gi, 'ap')
      .replace(/accounts receivables?/gi, 'ar')
      .replace(/time and labor/gi, 'time')
      .replace(/time & labor/gi, 'time')
      .replace(/learning management/gi, 'learning')
      .replace(/performance mgmt|performance management/gi, 'performance')
      .replace(/recruit cloud|irecruitment/gi, 'recruit')
      .replace(/payroll - united states/gi, 'payroll us')
      .replace(/-|_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Simple fuzzy match - check if words overlap
  const fuzzyMatch = (a, b) => {
    const aWords = a.split(/\s+/).filter(w => w.length > 2);
    const bWords = b.split(/\s+/).filter(w => w.length > 2);
    return aWords.some(w => bWords.includes(w));
  };

  // Toggle selection for a specific product type
  const toggleSelection = (productType, item) => {
    if (!item) return;

    let key;
    if (productType === 'ebs') {
      key = 'reportsEBS';
    } else if (productType === 'cloud') {
      key = 'reportsCloud';
    } else if (productType === 'analytics') {
      key = item.isPEPM ? 'peopleAnalytics' : 'financialAnalytics';
    }

    const current = quoteData[key] || [];
    const updated = current.includes(item.name)
      ? current.filter(n => n !== item.name)
      : [...current, item.name];

    setQuoteData({ ...quoteData, [key]: updated });
  };

  // Check if item is selected
  const isSelected = (productType, item) => {
    if (!item) return false;

    let key;
    if (productType === 'ebs') {
      key = 'reportsEBS';
    } else if (productType === 'cloud') {
      key = 'reportsCloud';
    } else if (productType === 'analytics') {
      key = item.isPEPM ? 'peopleAnalytics' : 'financialAnalytics';
    }

    return (quoteData[key] || []).includes(item.name);
  };

  // Format price for display
  const formatPrice = (item) => {
    if (!item) return '-';
    if (item.isPEPM && item.pepm !== undefined) {
      return `$${item.pepm.toFixed(2)}`;
    }
    if (item.price === 0) return 'Inc';
    return `$${(item.price / 1000).toFixed(1).replace('.0', '')}K`;
  };

  // Domain section component
  const DomainSection = ({ domain }) => {
    const allModules = getModulesForDomain(domain);
    // Filter modules by search query
    const modules = searchQuery.trim()
      ? allModules.filter(mod =>
          matchesSearch(mod.displayName) ||
          (mod.ebs && matchesSearch(mod.ebs.sku)) ||
          (mod.cloud && matchesSearch(mod.cloud.sku)) ||
          (mod.analytics && matchesSearch(mod.analytics.sku))
        )
      : allModules;
    const isExpanded = expandedDomains[domain] || (searchQuery.trim() && modules.length > 0);
    const showAnalytics = domain === 'hcm' || domain === 'fin';
    const analyticsLabel = domain === 'hcm' ? 'People Analytics' : 'Financial Analytics';
    const isPepm = domain === 'hcm';

    // Calculate totals for this domain
    const calculateTotal = () => {
      let total = 0;
      modules.forEach(mod => {
        if (mod.ebs && isSelected('ebs', mod.ebs)) {
          total += mod.ebs.price || 0;
        }
        if (mod.cloud && isSelected('cloud', mod.cloud)) {
          total += mod.cloud.price || 0;
        }
        if (mod.analytics && isSelected('analytics', mod.analytics)) {
          if (mod.analytics.isPEPM) {
            // PEPM will be calculated separately
          } else {
            total += mod.analytics.price || 0;
          }
        }
      });
      return total;
    };

    const domainTotal = calculateTotal();

    if (modules.length === 0) return null;

    return (
      <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
        {/* Header */}
        <button
          onClick={() => toggleDomain(domain)}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown size={20} className="text-slate-600" /> : <ChevronRight size={20} className="text-slate-600" />}
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-800">{domainLabels[domain]}</h3>
              <p className="text-xs text-slate-500">
                {searchQuery.trim() && modules.length !== allModules.length
                  ? `${modules.length} of ${allModules.length} modules match`
                  : `${modules.length} modules available`}
              </p>
            </div>
          </div>
          {domainTotal > 0 && (
            <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              ${(domainTotal / 1000).toFixed(1).replace('.0', '')}K selected
            </span>
          )}
        </button>

        {/* Matrix Table */}
        {isExpanded && (
          <div className="p-4 overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-slate-200 min-w-[200px]">
                    Module
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-blue-700 border-b border-slate-200 w-32">
                    <div>Reports</div>
                    <div className="text-xs font-normal text-blue-500">EBS</div>
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-indigo-700 border-b border-slate-200 w-32">
                    <div>Reports</div>
                    <div className="text-xs font-normal text-indigo-500">Cloud</div>
                  </th>
                  {showAnalytics && (
                    <th className="px-4 py-3 text-center font-semibold text-purple-700 border-b border-slate-200 w-36">
                      <div>{analyticsLabel}</div>
                      {isPepm && <div className="text-xs font-normal text-purple-500">PEPM</div>}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {modules.map((mod, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-4 py-2 font-medium text-slate-800 border-b border-slate-100">
                      {mod.displayName}
                    </td>
                    <ModuleCell
                      item={mod.ebs}
                      productType="ebs"
                      color="blue"
                    />
                    <ModuleCell
                      item={mod.cloud}
                      productType="cloud"
                      color="indigo"
                    />
                    {showAnalytics && (
                      <ModuleCell
                        item={mod.analytics}
                        productType="analytics"
                        color="purple"
                      />
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Module cell component
  const ModuleCell = ({ item, productType, color }) => {
    if (!item) {
      return (
        <td className="px-4 py-2 text-center border-b border-slate-100">
          <span className="text-slate-300">—</span>
        </td>
      );
    }

    const selected = isSelected(productType, item);

    const colorClasses = {
      blue: {
        selected: 'bg-blue-100 border-blue-500 text-blue-700',
        hover: 'hover:border-blue-300 hover:bg-blue-50'
      },
      indigo: {
        selected: 'bg-indigo-100 border-indigo-500 text-indigo-700',
        hover: 'hover:border-indigo-300 hover:bg-indigo-50'
      },
      purple: {
        selected: 'bg-purple-100 border-purple-500 text-purple-700',
        hover: 'hover:border-purple-300 hover:bg-purple-50'
      }
    };
    const c = colorClasses[color];

    return (
      <td className="px-4 py-2 text-center border-b border-slate-100">
        <button
          onClick={() => toggleSelection(productType, item)}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all min-w-[80px] ${
            selected
              ? c.selected
              : `bg-white border-slate-200 text-slate-600 ${c.hover}`
          }`}
        >
          {selected && <Check size={14} />}
          <span>{formatPrice(item)}</span>
          {item.isPEPM && <span className="text-[10px] opacity-70">PEPM</span>}
        </button>
        <div className="text-[10px] text-slate-400 mt-0.5">{item.sku}</div>
        {isExistingCustomer && selected && (
          <button
            onClick={(e) => { e.stopPropagation(); toggleExisting(item.sku); }}
            className={`mt-1 text-[10px] px-2 py-0.5 rounded-full font-medium transition-all ${
              isExistingProduct(item.sku)
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-slate-100 text-slate-500 hover:bg-green-50 hover:text-green-600'
            }`}
          >
            {isExistingProduct(item.sku) ? 'Owned' : 'Mark Owned'}
          </button>
        )}
      </td>
    );
  };

  // Flat section for connectors, pipelines, utilities
  const FlatSection = ({ section, title, description }) => {
    const allItems = pricing[section] || [];
    // Filter items by search query
    const items = searchQuery.trim()
      ? allItems.filter(item => matchesSearch(item.name) || matchesSearch(item.sku))
      : allItems;
    const isExpanded = expandedSections[section] || (searchQuery.trim() && items.length > 0);
    const selected = quoteData[section] || [];
    const selectedCount = selected.length;

    // Count locked items
    const lockedCount = items.filter(item => isItemLocked(item.sku) && selected.includes(item.name)).length;

    const total = selected.reduce((sum, name) => {
      const item = items.find(i => i.name === name);
      return sum + (item?.price || 0);
    }, 0);

    const toggleItem = (name, sku) => {
      // Prevent removing locked items
      if (isItemLocked(sku) && selected.includes(name)) {
        return; // Can't remove locked dependency
      }

      const updated = selected.includes(name)
        ? selected.filter(n => n !== name)
        : [...selected, name];
      setQuoteData({ ...quoteData, [section]: updated });
    };

    if (items.length === 0) return null;

    return (
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <button
          onClick={() => toggleSection(section)}
          className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            <div className="text-left">
              <h3 className="font-semibold text-slate-800">{title}</h3>
              <p className="text-xs text-slate-500">
                {description} • {searchQuery.trim() && items.length !== allItems.length
                  ? `${items.length} of ${allItems.length} match`
                  : `${items.length} available`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lockedCount > 0 && (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                <Lock size={10} />
                {lockedCount} required
              </span>
            )}
            {selectedCount > 0 && (
              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                {selectedCount} selected
              </span>
            )}
            {total > 0 && (
              <span className="text-sm font-semibold text-blue-600">
                ${(total / 1000).toFixed(1).replace('.0', '')}K
              </span>
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="p-4">
            {/* Show locked/required items first if any */}
            {items.some(item => isItemLocked(item.sku)) && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Lock size={12} />
                  Required Infrastructure (auto-included)
                  <InfoTooltip
                    text="These connectors/pipelines are automatically included because they're required by your selected modules. Cloud Reports need the Oracle Cloud Pipeline + Connector. EBS Reports need the EBS Connector."
                    position="right"
                    size={12}
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {items.filter(item => isItemLocked(item.sku)).map(item => {
                    const itemSelected = selected.includes(item.name);
                    const locked = isItemLocked(item.sku);
                    const lockedReason = getLockedReason(item.sku);

                    return (
                      <div
                        key={item.sku}
                        title={lockedReason || item.name}
                        className="p-3 rounded-lg border-2 text-left border-amber-400 bg-amber-50 cursor-not-allowed"
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 border-amber-500 bg-amber-500">
                            <Lock size={10} className="text-white" />
                          </div>
                          <span className="font-medium text-amber-800 text-sm leading-tight">{item.name}</span>
                        </div>
                        <div className="flex justify-between mt-2 text-xs">
                          <span className="text-amber-600">{item.sku}</span>
                          <span className="text-amber-700 font-semibold">
                            {item.price === 0 ? 'Included' : `$${(item.price / 1000).toFixed(1).replace('.0', '')}K`}
                          </span>
                        </div>
                        {lockedReason && (
                          <div className="mt-2 text-[10px] text-amber-600 truncate" title={lockedReason}>
                            {lockedReason}
                          </div>
                        )}
                        {isExistingCustomer && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleExisting(item.sku); }}
                            className={`mt-2 w-full text-[10px] px-2 py-1 rounded font-medium transition-all ${
                              isExistingProduct(item.sku)
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-amber-50 text-amber-600 hover:bg-green-50 hover:text-green-600'
                            }`}
                          >
                            {isExistingProduct(item.sku) ? 'Owned' : 'Mark Owned'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other available items */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {items.filter(item => !isItemLocked(item.sku)).map(item => {
                const itemSelected = selected.includes(item.name);
                return (
                  <button
                    key={item.sku}
                    onClick={() => toggleItem(item.name, item.sku)}
                    title={item.name}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      itemSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        itemSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                      }`}>
                        {itemSelected && <Check size={10} className="text-white" />}
                      </div>
                      <span className="font-medium text-slate-700 text-sm leading-tight">{item.name}</span>
                    </div>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="text-slate-400">{item.sku}</span>
                      <span className={itemSelected ? 'text-blue-600 font-semibold' : 'text-slate-500'}>
                        {item.price === 0 ? 'Included' : `$${(item.price / 1000).toFixed(1).replace('.0', '')}K`}
                      </span>
                    </div>
                    {isExistingCustomer && itemSelected && (
                      <div
                        onClick={(e) => { e.stopPropagation(); toggleExisting(item.sku); }}
                        className={`mt-2 w-full text-[10px] px-2 py-1 rounded font-medium text-center transition-all ${
                          isExistingProduct(item.sku)
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : 'bg-slate-100 text-slate-500 hover:bg-green-50 hover:text-green-600'
                        }`}
                      >
                        {isExistingProduct(item.sku) ? 'Owned' : 'Mark Owned'}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
        Products
        <InfoTooltip
          text="Select modules by domain. Reports EBS connects to Oracle E-Business Suite on-premise. Reports Cloud connects to Oracle Fusion Cloud. Analytics provides advanced visualizations and PEPM pricing for HCM."
          position="right"
        />
      </h2>
      <p className="text-slate-600 mb-4">
        Select modules by domain. Columns show available products: Reports (EBS/Cloud) and Analytics.
      </p>

      {isExistingCustomer && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">
            <strong>Existing Customer:</strong> Select products, then mark already-owned items as "Owned". Owned products will appear on the quote at $0.
          </p>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search products, modules, connectors, pipelines..."
          className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Infrastructure - ERP Connectors & Data Pipelines first */}
      <div className="space-y-4 mb-8">
        <FlatSection
          section="erpConnectors"
          title="ERP Connectors"
          description="Oracle ERP system connectors"
        />
        <FlatSection
          section="dataPipelines"
          title="Data Pipelines"
          description="ETL and data integration"
        />
      </div>

      {/* Domain Matrices */}
      <div className="mb-8">
        <DomainSection domain="hcm" />
        <DomainSection domain="fin" />
        <DomainSection domain="scm" />
        <DomainSection domain="crm" />
      </div>

      {/* Other Sections */}
      <div className="space-y-4">
        {/* SQL Connect - inlined to prevent remount on typing */}
        {(() => {
          const sqlConnectUsers = quoteData.users?.sqlConnectEnt || 0;
          const sqlPrice = pricing.users?.sqlConnectEnt?.price || 600;
          const sqlSku = pricing.users?.sqlConnectEnt?.sku || 'BE:USER_SQLC_ENT';
          const isSqlExpanded = expandedSections.sqlConnect;
          return (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('sqlConnect')}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100"
              >
                <div className="flex items-center gap-3">
                  {isSqlExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-800">SQL Connect</h3>
                    <p className="text-xs text-slate-500">Database query tool - Enterprise</p>
                  </div>
                </div>
                {sqlConnectUsers > 0 && (
                  <span className="text-sm font-semibold text-blue-600">
                    {sqlConnectUsers} users • ${(sqlConnectUsers * sqlPrice).toLocaleString()}
                  </span>
                )}
              </button>
              {isSqlExpanded && (
                <div className="p-4">
                  <div className="p-4 rounded-lg border border-slate-200 bg-white">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-slate-800">SQL Connect Enterprise</h4>
                        <p className="text-sm text-slate-600 mt-1">Database query tool for power users</p>
                        <p className="text-xs text-slate-400 mt-1">SKU: {sqlSku}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-600">${sqlPrice.toLocaleString()}</div>
                        <div className="text-xs text-slate-500">/user/year</div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Number of Users</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={sqlConnectUsers > 0 ? String(sqlConnectUsers) : ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setQuoteData({
                            ...quoteData,
                            users: { ...quoteData.users, sqlConnectEnt: parseInt(val) || 0 }
                          });
                        }}
                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-medium"
                        placeholder="Enter number of users"
                      />
                    </div>
                    {sqlConnectUsers > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200 text-right">
                        <span className="text-sm text-slate-600">Subtotal: </span>
                        <span className="font-bold text-slate-800">${(sqlConnectUsers * sqlPrice).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {/* GL Connect - inlined to prevent remount on typing */}
        {(() => {
          const gl = pricing.glConnect || {};
          const glConnectUsers = quoteData.users?.glConnect || 0;
          const glUserPrice = pricing.users?.glConnect?.price || 1500;
          const glUserSku = pricing.users?.glConnect?.sku || 'BE:USER_SPLASHGL_POWER';
          const hasGLProduct = quoteData.glConnectOnPremise || quoteData.glConnectCloud;
          const isGlExpanded = expandedSections.glConnect;
          if (!gl.onPremise && !gl.cloud) return null;
          return (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('glConnect')}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100"
              >
                <div className="flex items-center gap-3">
                  {isGlExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-800">GL Connect</h3>
                    <p className="text-xs text-slate-500">General Ledger integration + user licenses</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasGLProduct && (
                    <span className="text-xs px-2 py-1 rounded-full bg-teal-100 text-teal-700">
                      {quoteData.glConnectOnPremise && quoteData.glConnectCloud ? '2 products' : '1 product'}
                    </span>
                  )}
                  {glConnectUsers > 0 && (
                    <span className="text-sm font-semibold text-blue-600">
                      {glConnectUsers} users
                    </span>
                  )}
                </div>
              </button>
              {isGlExpanded && (
                <div className="p-4 space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">GL Connect Products</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {gl.onPremise && (
                        <button
                          onClick={() => setQuoteData({...quoteData, glConnectOnPremise: !quoteData.glConnectOnPremise})}
                          className={`p-4 rounded-lg border-2 text-left ${
                            quoteData.glConnectOnPremise
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 hover:border-teal-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              quoteData.glConnectOnPremise ? 'border-teal-500 bg-teal-500' : 'border-slate-300'
                            }`}>
                              {quoteData.glConnectOnPremise && <Check size={10} className="text-white" />}
                            </div>
                            <span className="font-semibold text-slate-800">Oracle EBS</span>
                          </div>
                          <div className="text-xs text-slate-400">{gl.onPremise.sku}</div>
                          <div className="text-sm font-semibold text-teal-600 mt-1">
                            {gl.onPremise.price === 0 ? 'Included' : `$${(gl.onPremise.price / 1000).toFixed(1).replace('.0', '')}K`}
                          </div>
                          {isExistingCustomer && quoteData.glConnectOnPremise && (
                            <div
                              onClick={(e) => { e.stopPropagation(); toggleExisting(gl.onPremise.sku); }}
                              className={`mt-2 w-full text-[10px] px-2 py-1 rounded font-medium text-center transition-all ${
                                isExistingProduct(gl.onPremise.sku)
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : 'bg-slate-100 text-slate-500 hover:bg-green-50 hover:text-green-600'
                              }`}
                            >
                              {isExistingProduct(gl.onPremise.sku) ? 'Owned' : 'Mark Owned'}
                            </div>
                          )}
                        </button>
                      )}
                      {gl.cloud && (
                        <button
                          onClick={() => setQuoteData({...quoteData, glConnectCloud: !quoteData.glConnectCloud})}
                          className={`p-4 rounded-lg border-2 text-left ${
                            quoteData.glConnectCloud
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 hover:border-teal-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              quoteData.glConnectCloud ? 'border-teal-500 bg-teal-500' : 'border-slate-300'
                            }`}>
                              {quoteData.glConnectCloud && <Check size={10} className="text-white" />}
                            </div>
                            <span className="font-semibold text-slate-800">Oracle Fusion Cloud</span>
                          </div>
                          <div className="text-xs text-slate-400">{gl.cloud.sku}</div>
                          <div className="text-sm font-semibold text-teal-600 mt-1">
                            {gl.cloud.price === 0 ? 'Included' : `$${(gl.cloud.price / 1000).toFixed(1).replace('.0', '')}K`}
                          </div>
                          {isExistingCustomer && quoteData.glConnectCloud && (
                            <div
                              onClick={(e) => { e.stopPropagation(); toggleExisting(gl.cloud.sku); }}
                              className={`mt-2 w-full text-[10px] px-2 py-1 rounded font-medium text-center transition-all ${
                                isExistingProduct(gl.cloud.sku)
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : 'bg-slate-100 text-slate-500 hover:bg-green-50 hover:text-green-600'
                              }`}
                            >
                              {isExistingProduct(gl.cloud.sku) ? 'Owned' : 'Mark Owned'}
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">GL Connect User Licenses</h4>
                    <div className="p-4 rounded-lg border border-slate-200 bg-white">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-slate-800">GL Connect Users</h4>
                          <p className="text-sm text-slate-600 mt-1">Financial statement builder access</p>
                          <p className="text-xs text-slate-400 mt-1">SKU: {glUserSku}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">${glUserPrice.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">/user/year</div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Number of Users</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={glConnectUsers > 0 ? String(glConnectUsers) : ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setQuoteData({
                              ...quoteData,
                              users: { ...quoteData.users, glConnect: parseInt(val) || 0 }
                            });
                          }}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-medium"
                          placeholder="Enter number of users"
                        />
                      </div>
                      {glConnectUsers > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200 text-right">
                          <span className="text-sm text-slate-600">Subtotal: </span>
                          <span className="font-bold text-slate-800">${(glConnectUsers * glUserPrice).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        <FlatSection
          section="utilities"
          title="Utilities"
          description="Migration and tools"
        />
      </div>
    </div>
  );
};

export default ProductsStepNested;
